import { useCallback, useSyncExternalStore } from 'react';

import type { ViewPagePerformanceModeState } from './viewPagePerformanceMode.ts';

type Snapshot = {
	state: ViewPagePerformanceModeState;
	transitionId: number;
};

let snapshot: Snapshot | null = null;
let publisher: symbol | null = null;
let lastVisitContentId: string | null = null;
let notificationClaimed = false;
const subscribers = new Set<() => void>();

const notify = (): void => subscribers.forEach((subscriber) => subscriber());
const getServerSnapshot = (): null => null;
const subscribe = (subscriber: () => void): (() => void) => {
	subscribers.add(subscriber);
	return () => subscribers.delete(subscriber);
};

// Publish only after commit. The renderer derives its own decision synchronously; this store
// bridges that decision to the lazy Page Details panel without re-rendering the renderer.
export const publishViewPagePerformanceMode = (
	owner: symbol,
	state: ViewPagePerformanceModeState,
	transitionId: number,
): void => {
	// A visit survives query/hash navigation and renderer remounts. The route listener
	// ends it when the reader leaves the page, even if the destination has no ADF renderer.
	if (lastVisitContentId !== state.contentId) {
		lastVisitContentId = state.contentId;
		notificationClaimed = false;
	}
	publisher = owner;
	snapshot = { state, transitionId };
	notify();
};

export const clearViewPagePerformanceMode = (owner: symbol): void => {
	if (publisher === owner) {
		publisher = null;
		snapshot = null;
		notify();
	}
};

export const endViewPagePerformanceModeVisit = (contentId: string): void => {
	if (lastVisitContentId === contentId) {
		lastVisitContentId = null;
		notificationClaimed = false;
	}
	if (snapshot?.state.contentId === contentId) {
		publisher = null;
		snapshot = null;
		notify();
	}
};

export const claimViewPagePerformanceModeNotification = (
	owner: symbol,
	contentId: string,
	transitionId: number,
): boolean => {
	if (
		publisher !== owner ||
		!snapshot?.state.isActive ||
		snapshot.state.contentId !== contentId ||
		snapshot.transitionId !== transitionId ||
		notificationClaimed
	) {
		return false;
	}
	notificationClaimed = true;
	return true;
};

export const useViewPagePerformanceMode = (
	contentId: string | undefined,
	transitionId: number,
): ViewPagePerformanceModeState | null => {
	const getSnapshot = useCallback(
		() =>
			snapshot && snapshot.state.contentId === contentId && snapshot.transitionId === transitionId
				? snapshot.state
				: null,
		[contentId, transitionId],
	);
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

export const clearViewPagePerformanceModeForTesting = (): void => {
	snapshot = null;
	publisher = null;
	lastVisitContentId = null;
	notificationClaimed = false;
	subscribers.clear();
};
